class ARProductViewer {
  constructor() {
    console.group('=== AR Viewer Initialization ===');
    this.init();
    console.groupEnd();
  }

  async init() {
    this.debug = true;
    this.logEvent('Initializing AR Viewer');

    this.state = {
      initialized: false,
      currentModelId: null,
      currentVariant: null,
      eventsBound: false,
      currentSize: null,
      currentFrame: null
    };

    await this.initializeElements();
    await this.initializeShopifyXR();
    this.setupEventListeners();
    await this.handleInitialVariant();
  }

  async initializeElements() {
    this.arButton = document.querySelector('.product-single__view-in-ar');
    this.variantSelector = document.querySelector('variant-selects');
    this.sizeRadios = document.querySelectorAll('input[type="radio"][name^="Size"], input[type="radio"][name^="Tamaño"]');
    this.frameRadios = document.querySelectorAll('input[type="radio"][name^="Frame"], input[type="radio"][name^="Marco"]');
    
    this.logEvent('Elements Found', {
      arButton: !!this.arButton,
      buttonDisplay: this.arButton?.style.display,
      buttonId: this.arButton?.getAttribute('data-shopify-model3d-id'),
      variantSelector: !!this.variantSelector,
      sizeRadios: this.sizeRadios.length,
      frameRadios: this.frameRadios.length
    });

    const modelUrlsElement = document.getElementById('ModelURLs');
    this.modelURLs = window.productModels || {};
    this.logEvent('3D Models Available', this.modelURLs);
  }

  async initializeShopifyXR() {
    this.logEvent('Initializing ShopifyXR');

    window.ShopifyXR = window.ShopifyXR || function() {
      (ShopifyXR.q = ShopifyXR.q || []).push(arguments);
    };
    
    return new Promise((resolve) => {
      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: () => {
            this.logEvent('Model Viewer UI Loaded');
            
            window.Shopify.loadFeatures([
              {
                name: 'shopify-xr',
                version: '1.0',
                onLoad: () => {
                  this.logEvent('ShopifyXR Loaded');
                  this.setupShopifyXR();
                  resolve();
                }
              }
            ]);
          }
        }
      ]);

      document.addEventListener('shopify_xr_initialized', () => {
        this.logEvent('ShopifyXR Initialized Event Received');
      });

      document.addEventListener('shopify_xr_launch', () => {
        this.logEvent('ShopifyXR Launch Event Received');
      });
    });
  }

  setupShopifyXR() {
    this.logEvent('Setting up ShopifyXR Models');
    
    try {
      const models = Object.entries(this.modelURLs).map(([id, url]) => ({
        type: "model-viewer",
        id,
        src: url,
        variants: [{
          id,
          url,
          format: 'glb'
        }]
      }));

      this.logEvent('Registering AR Models', models);

      if (window.ShopifyXR) {
        window.ShopifyXR.addModels(models);
        window.ShopifyXR.setupXRElements();
      }
      
      this.logEvent('ShopifyXR Setup Complete');
    } catch (e) {
      console.error('ShopifyXR Setup Error:', e);
    }
  }

  setupEventListeners() {
    this.logEvent('Setting up Event Listeners');

    if (this.arButton) {
      this.arButton.addEventListener('click', (e) => {
        this.logEvent('AR Button Clicked', {
          modelId: this.arButton.getAttribute('data-shopify-model3d-id'),
          display: this.arButton.style.display,
          currentSize: this.state.currentSize,
          currentFrame: this.state.currentFrame
        });
        
        e.preventDefault();
        const modelId = this.arButton.getAttribute('data-shopify-model3d-id');
        const modelUrl = this.modelURLs[modelId];
        
        if (modelId && window.ShopifyXR) {
          window.ShopifyXR.loadXRScene(modelId, {
            modelId,
            url: modelUrl
          });
        }
      });
    }

    this.sizeRadios.forEach(radio => {
      radio.addEventListener('change', (event) => {
        this.state.currentSize = event.target.value;
        this.logEvent('Size Variant Changed', {
          name: event.target.name,
          value: event.target.value,
          currentFrame: this.state.currentFrame
        });
        this.updateARVisibility();
      });
    });

    this.frameRadios.forEach(radio => {
      radio.addEventListener('change', (event) => {
        this.state.currentFrame = event.target.value;
        this.logEvent('Frame Variant Changed', {
          name: event.target.name,
          value: event.target.value,
          currentSize: this.state.currentSize
        });
        this.updateARVisibility();
      });
    });

    document.addEventListener('shopify_xr_launch', () => {
      this.logEvent('ShopifyXR Scene Launched', {
        currentModelId: this.state.currentModelId,
        currentSize: this.state.currentSize,
        currentFrame: this.state.currentFrame
      });
    });

    document.addEventListener('shopify_xr_initialized', () => {
      this.logEvent('ShopifyXR System Initialized');
    });

    this.state.eventsBound = true;
  }

  updateARVisibility() {
    this.logEvent('Checking AR Visibility', {
      currentSize: this.state.currentSize,
      currentFrame: this.state.currentFrame
    });

    if (!this.state.currentSize || !this.state.currentFrame) {
      this.logEvent('Missing variant information', {
        size: this.state.currentSize,
        frame: this.state.currentFrame
      });
      return;
    }

    if (this.isARCompatible(this.state.currentSize, this.state.currentFrame)) {
      const modelId = this.getModelId(this.state.currentSize, this.state.currentFrame);
      if (modelId && this.modelURLs[modelId]) {
        this.showARButton(modelId);
      } else {
        this.hideARButton();
      }
    } else {
      this.hideARButton();
    }
  }

  isARCompatible(size, frame) {
    if (!size || !frame) return false;

    const result = {
      size,
      frame,
      isDigital: size.toLowerCase().includes('digital'),
      is15x20BlackFrame: size.includes('15x20') && 
                        (frame.toLowerCase().includes('black') || 
                         frame.toLowerCase().includes('negro'))
    };

    result.hasValidSize = ['15x20', '21x30', '30x40'].some(validSize => 
      size.toLowerCase().includes(validSize)
    );

    const isCompatible = result.hasValidSize && 
                        !result.isDigital && 
                        !result.is15x20BlackFrame;

    this.logEvent('AR Compatibility Check', {
      ...result,
      isCompatible
    });

    return isCompatible;
  }

  getModelId(size, frame) {
    if (!size || !frame) return null;

    const sizeMatch = size.match(/(\d+x\d+)/);
    if (!sizeMatch) return null;

    const sizeNumbers = sizeMatch[1];
    const hasFrame = frame.toLowerCase().includes('black') || 
                    frame.toLowerCase().includes('negro');
    
    const modelId = hasFrame ? `frame_${sizeNumbers}` : `noframe_${sizeNumbers}`;

    this.logEvent('Model ID Generated', {
      size,
      frame,
      sizeNumbers,
      hasFrame,
      modelId,
      modelExists: !!this.modelURLs[modelId]
    });

    return modelId;
  }

  async handleInitialVariant() {
    this.logEvent('Handling Initial Variant');
    
    try {
      const variantJson = this.variantSelector.querySelector('script[type="application/json"]');
      if (!variantJson) {
        throw new Error('No variant JSON found');
      }

      const variants = JSON.parse(variantJson.textContent);
      const initialVariant = variants.find(v => v.available);
      
      this.logEvent('Initial Variant Data', {
        variant: initialVariant,
        totalVariants: variants.length
      });
      
      if (initialVariant) {
        const [size, frame] = initialVariant.title.split(' / ');
        this.state.currentSize = size;
        this.state.currentFrame = frame;
        this.updateARVisibility();
      }
    } catch (e) {
      console.error('Error handling initial variant:', e);
    }
  }

  showARButton(modelId) {
    this.logEvent('Showing AR Button', {
      previousModelId: this.state.currentModelId,
      newModelId: modelId,
      currentSize: this.state.currentSize,
      currentFrame: this.state.currentFrame,
      modelUrl: this.modelURLs[modelId]
    });

    this.state.currentModelId = modelId;
    this.arButton.style.setProperty('display', 'flex', 'important');
    
    this.arButton.setAttribute('data-shopify-model3d-id', modelId);
    this.arButton.setAttribute('data-shopify-title', modelId);
    this.arButton.setAttribute('data-shopify-xr', '');
    this.arButton.setAttribute('data-shopify-model3d-url', this.modelURLs[modelId]);
    
    const isSpanish = document.documentElement.lang.includes('es');
    this.arButton.textContent = isSpanish ? 'Visualízalo en tu pared' : 'View in your own wall';

    if (window.ShopifyXR) {
      window.ShopifyXR.setupXRElements();
    }
  }

  hideARButton() {
    this.logEvent('Hiding AR Button', {
      currentSize: this.state.currentSize,
      currentFrame: this.state.currentFrame
    });

    this.state.currentModelId = null;
    this.arButton.style.setProperty('display', 'none', 'important');
    this.arButton.removeAttribute('data-shopify-model3d-id');
  }

  logEvent(title, data = null) {
    if (!this.debug) return;
    console.group(`🔍 ${title}`);
    if (data) console.log(JSON.stringify(data, null, 2));
    console.trace();
    console.groupEnd();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.arViewer = new ARProductViewer();
  
  window.debugAR = {
    getState: () => window.arViewer?.state,
    getModels: () => window.arViewer?.modelURLs,
    getCurrentVariants: () => ({
      size: window.arViewer?.state.currentSize,
      frame: window.arViewer?.state.currentFrame
    }),
    triggerCheck: () => window.arViewer?.updateARVisibility(),
    reloadViewer: () => {
      window.arViewer = new ARProductViewer();
    },
    radioButtons: {
      getSizeRadios: () => [...window.arViewer?.sizeRadios].map(radio => ({
        value: radio.value,
        checked: radio.checked,
        name: radio.name
      })),
      getFrameRadios: () => [...window.arViewer?.frameRadios].map(radio => ({
        value: radio.value,
        checked: radio.checked,
        name: radio.name
      })),
      getCurrentSelection: () => ({
        size: [...window.arViewer?.sizeRadios].find(r => r.checked)?.value || null,
        frame: [...window.arViewer?.frameRadios].find(r => r.checked)?.value || null
      })
    }
  };
});